import {
  AlertTriangle,
  Archive,
  BarChart2,
  BarChart3,
  Bell,
  BookCheck,
  BookUser,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarMinus,
  CalendarOff,
  CalendarRange,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Crown,
  FileText,
  GraduationCap,
  Handshake,
  HeartPulse,
  IdCard,
  Landmark,
  Laptop,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  LogOut,
  Mail,
  Megaphone,
  Newspaper,
  Package,
  Plug,
  Scale,
  ScanLine,
  ScrollText,
  Send,
  Settings,
  ShoppingBag,
  Sliders,
  Sparkles,
  Stethoscope,
  Thermometer,
  Users,
  UtensilsCrossed,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { AdminImage } from '@/components/AdminImage';
import { AttentionBadge } from '@/components/ui/attention-badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { canAccess, posOperatorMayVisit } from '@/lib/access';
import { APP_VERSION, cleanVersionTag } from '@/lib/app-version';
import { Role, User } from '@/lib/auth-context';
import type { ModuleKey } from '@/lib/modules';
import { useAuth } from '@/lib/use-auth';
import { cn } from '@/lib/utils';
import { useTeachesAnyQuery } from '@/services/api/attendance/hooks/use-attendance';
import { useMyProfileQuery } from '@/services/api/profile/hooks/use-profile';

import { useNavBadges } from './use-nav-badges';

type NavItem = {
  name: string;
  href: string;
  icon: React.ElementType;
  module?: ModuleKey;
  /** Visible to anyone holding at least one of these — a row whose page carries a tab per grant. */
  anyModule?: ModuleKey[];
  /** With `anyModule`: administrators see the row too, for the tabs that are theirs alone. */
  orAdmin?: boolean;
  adminOnly?: boolean;
  /** Bypasses `canAccess` entirely: visible to any logged-in staff user (admin/teacher/
   *  employee), regardless of granted modules — but NOT to student/parent. Use for
   *  all-staff read-only tools like the Student Directory. Does not affect `canAccess`'s
   *  behavior for other nav items. */
  staffOnly?: boolean;
  /** Hidden from people who hold this module: the row is the personal half of a page whose
   *  admin half already appears in its own section (Incidents is both). */
  hiddenWhenModule?: ModuleKey;
  /** Offered to every teacher, and to anyone else who actually teaches — asked of the timetable,
   *  since an admin may teach. A teacher with no classes keeps the row for its Diary tab. */
  teachingOnly?: boolean;
  /** Visible to admins and teachers only. Narrower than `staffOnly`, which also admits
   *  employees — use where the API itself is @Roles(admin, teacher) and an employee would
   *  reach the page only to have every request 403. */
  teachingStaffOnly?: boolean;
};
type NavSection = { section: string; items: NavItem[]; defaultOpen?: boolean };
type NavEntry = NavItem | NavSection;

function isSection(entry: NavEntry): entry is NavSection {
  return 'section' in entry;
}

/**
 * Which sections are collapsed, remembered per browser.
 *
 * Stored as the collapsed set rather than the open one, so sections added later start open —
 * a new area appearing is worth noticing, and nobody has to go looking for it.
 */
const COLLAPSED_KEY = 'risp-admin.sidebar.collapsed';

function readCollapsed(): string[] {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function useCollapsedSections() {
  const [collapsed, setCollapsed] = useState<string[]>(() => readCollapsed());

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed));
    } catch {
      // A full or blocked storage must never break navigation; the choice just won't persist.
    }
  }, [collapsed]);

  const toggle = useCallback((section: string) => {
    setCollapsed(prev => (prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]));
  }, []);

  const expand = useCallback((section: string) => {
    setCollapsed(prev => (prev.includes(section) ? prev.filter(s => s !== section) : prev));
  }, []);

  return { collapsed, toggle, expand };
}

const nav: NavEntry[] = [
  { name: 'nav.dashboard', href: '/', icon: LayoutDashboard },
  {
    // Everything a person does for themselves, first because for a teacher it is most of
    // the app. The section header carries the "my", so the rows do not repeat it.
    section: 'nav.section.my_work',
    items: [
      { name: 'nav.my_work.classes_today', href: '/my-classes', icon: ClipboardCheck, teachingOnly: true },
      // Same route serves both: a teacher sees only their own entries, an admin sees those
      // first and the whole issue below. It sits here because writing them is personal work.
      { name: 'nav.my_work.newsletter', href: '/newsletter', icon: FileText },
      { name: 'nav.communications', href: '/communications', icon: Send, module: 'communications' },
      { name: 'nav.my_work.bookings', href: '/bookings/mine', icon: CalendarCheck, staffOnly: true },
      { name: 'nav.my_work.leaves', href: '/my-leaves', icon: CalendarMinus },
      { name: 'nav.my_work.it_tickets', href: '/it-tickets/mine', icon: LifeBuoy, staffOnly: true },
      { name: 'nav.my_work.edp', href: '/edp/mine', icon: CalendarClock, staffOnly: true },
      { name: 'nav.sparky', href: '/sparky', icon: Laptop, module: 'sparky' },
      // The SST sees the full queue under Student instead; this row covers the states relevant
      // to the teacher — cases they're attached to, or involving their homeroom class.
      { name: 'nav.my_work.sst', href: '/sst', icon: AlertTriangle, hiddenWhenModule: 'sst' },
    ],
  },
  {
    section: 'nav.section.website',
    items: [
      { name: 'nav.news', href: '/news', icon: Newspaper, adminOnly: true },
      { name: 'nav.school_calendar', href: '/calendar-admin', icon: CalendarDays, module: 'calendar' },
      { name: 'nav.thai_events', href: '/thai-events', icon: Crown, module: 'thai-events' },
      { name: 'nav.announcements', href: '/announcements', icon: Megaphone, module: 'announcements' },
      // Teachers and the weekly menu are website pages too — /our-teachers and /family/weekly-menu
      // — so they sit with the rest of what gets published rather than under a heading of their own.
      { name: 'nav.teachers', href: '/teachers', icon: GraduationCap, module: 'teachers' },
      { name: 'nav.weekly_menus', href: '/weekly-menus', icon: UtensilsCrossed, module: 'weekly-menus' },
      // Below the line: what the website sends back, rather than what goes out on it.
      { name: 'nav.statistics', href: '/statistics', icon: BarChart2, module: 'statistics' },
      { name: 'nav.inquiries', href: '/inquiries', icon: Mail, anyModule: ['contact-inquiries', 'tour-requests'] },
    ],
  },
  {
    section: 'nav.section.sand_dollars',
    items: [
      { name: 'nav.sand_dollars_award', href: '/sand-dollars/award', icon: Sparkles },
      { name: 'nav.sand_dollars_records', href: '/sand-dollars', icon: ListChecks },
      { name: 'nav.sand_dollars_config', href: '/sand-dollars/config', icon: Sliders, module: 'sand-dollars-config' },
      { name: 'nav.house_points', href: '/sand-dollars/house-points', icon: Scale, module: 'sand-dollars-config' },
    ],
  },
  {
    section: 'nav.section.hr',
    items: [
      { name: 'nav.leave_requests', href: '/leave-requests', icon: CalendarOff, module: 'leave-requests' },
      {
        name: 'nav.immigration_compliance',
        href: '/immigration-compliance',
        icon: IdCard,
        module: 'immigration-compliance',
      },
    ],
  },
  {
    section: 'nav.section.edp',
    items: [
      // The schedule, the queue and registrations are one screen with three tabs: the office
      // moves between them constantly, and three rows asked it to know which one its next
      // click was in.
      { name: 'nav.edp', href: '/edp', icon: CalendarRange, module: 'edp' },
    ],
  },
  {
    section: 'nav.section.it_support',
    items: [
      { name: 'nav.it_board', href: '/it-tickets', icon: LayoutDashboard, module: 'it-support' },
      { name: 'nav.it_archive', href: '/it-tickets/archive', icon: Archive, module: 'it-support' },
      { name: 'nav.it_assets', href: '/it-assets', icon: Laptop, module: 'it-support' },
    ],
  },
  {
    section: 'nav.section.bookings',
    items: [{ name: 'nav.all_bookings', href: '/bookings', icon: LayoutDashboard, module: 'bookings' }],
  },
  {
    section: 'nav.section.student',
    items: [
      { name: 'nav.students', href: '/students', icon: BookUser, staffOnly: true },
      { name: 'nav.student_absences', href: '/student-absences', icon: CalendarOff },
      { name: 'nav.arrivals_dismissals', href: '/arrivals-dismissals', icon: Clock },
      { name: 'nav.sst', href: '/sst', icon: AlertTriangle, module: 'sst' },
      // Not module-gated: the office holds `report-cards`, teachers do not, and this row is how
      // a teacher with grades to enter reaches the page at all. The module still gates the
      // Periods and Completeness tabs behind it — see `ReportCardsPage` and `nav.edp_afternoon`
      // above, which makes the same call for the same reason. Admin/teacher only: the entry API
      // is @Roles(admin, teacher), so an employee shown this row would 403 on every read.
      { name: 'nav.report_cards', href: '/report-cards', icon: ClipboardList, teachingStaffOnly: true },
    ],
  },
  {
    // What happens to students day to day. For a teacher this is the whole of their
    // admin-side work, so it says what it is rather than "Administration".
    section: 'nav.section.attendance',
    items: [
      // Setup is reached from the gear on the page itself, shown to holders of the module only.
      { name: 'nav.timetable', href: '/timetable', icon: CalendarRange },
      { name: 'nav.attendance', href: '/attendance', icon: ClipboardCheck, teachingStaffOnly: true },
      // Handshake rather than a door: the handover to a waiting parent is what this screen is
      // actually for, and a door icon would read as the door-access integration.
      { name: 'nav.checkout', href: '/attendance/checkout', icon: Handshake, teachingStaffOnly: true },
      // Beside Checkout, which is the same question an hour earlier: who goes home now and who
      // stays. Not module-gated, unlike the office's EDP screen — the API grants this to any
      // admin/teacher/employee (`edp.controller.ts`'s `afternoon` endpoint), so gating it would
      // hide it from every homeroom teacher who does not also hold the EDP module.
      { name: 'nav.edp_afternoon', href: '/edp/afternoon', icon: Clock, staffOnly: true },
      // The school-wide register grid. A teacher's own registers are under Classes & diary, and the
      // audit tab still links into this page for any register it lists.
      { name: 'nav.period_attendance', href: '/attendance/period', icon: BookCheck, adminOnly: true },
      { name: 'nav.attendance_report', href: '/attendance/report', icon: BarChart2, teachingStaffOnly: true },
    ],
  },
  {
    // The adults' records: everything else about a student lives under Student.
    section: 'nav.section.people',
    items: [
      { name: 'nav.parents', href: '/parents', icon: Users, module: 'parents' },
      { name: 'nav.users', href: '/users', icon: Users, module: 'users' },
    ],
  },
  {
    section: 'nav.section.cafeteria',
    items: [
      { name: 'nav.cafeteria', href: '/cafeteria', icon: UtensilsCrossed, module: 'cafeteria' },
      { name: 'nav.cafeteria_pos', href: '/cafeteria/pos', icon: ScanLine, module: 'cafeteria' },
    ],
  },
  {
    section: 'nav.section.tuition',
    items: [{ name: 'nav.tuition', href: '/tuition', icon: Landmark, module: 'billing' }],
  },
  {
    section: 'nav.section.shop',
    items: [
      { name: 'nav.shop', href: '/shop', icon: ShoppingBag, module: 'shop' },
      { name: 'nav.shop_pos', href: '/shop/pos', icon: ScanLine, module: 'shop' },
      { name: 'nav.shop_inventory', href: '/shop/inventory', icon: Package, module: 'shop' },
      { name: 'nav.shop_reports', href: '/shop/reports', icon: BarChart3, module: 'shop' },
    ],
  },
  {
    section: 'nav.section.medical',
    items: [
      { name: 'nav.medical_hub', href: '/medical', icon: Stethoscope, module: 'medical' },
      { name: 'nav.temperature', href: '/temperature', icon: Thermometer, module: 'medical' },
      { name: 'nav.infirmary_log', href: '/medical/log', icon: ClipboardList, module: 'medical' },
      {
        name: 'nav.allergy_change_requests',
        href: '/medical/allergy-change-requests',
        icon: HeartPulse,
        module: 'medical',
      },
    ],
  },
  {
    section: 'nav.section.config',
    items: [
      // School days and sessions are tabs of the year they shape, for administrators.
      {
        name: 'nav.school_years',
        href: '/school-years',
        icon: CalendarRange,
        anyModule: ['school-years'],
        orAdmin: true,
      },
      { name: 'nav.settings', href: '/settings', icon: Settings, module: 'settings' },
      { name: 'nav.logs', href: '/logs', icon: ScrollText, module: 'audit-log' },
      { name: 'nav.integration', href: '/integration', icon: Plug, adminOnly: true },
      { name: 'nav.bell_schedule', href: '/integration/bell-schedule', icon: Bell, adminOnly: true },
    ],
  },
];

const NAV_HREFS = nav.flatMap(e => (isSection(e) ? e.items.map(i => i.href) : [e.href]));

/**
 * The single best-matching nav href for a path: the longest href that equals
 * the path or is a path-segment prefix of it. This keeps exactly one item
 * highlighted when nav entries are nested (e.g. /attendance vs
 * /attendance/school-calendar) while still highlighting the parent for
 * drill-in routes that have no nav item of their own (e.g. /attendance/person/x).
 */
function activeNavHref(pathname: string): string {
  let best = '';
  for (const href of NAV_HREFS) {
    const matches = href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(href + '/');
    if (matches && href.length > best.length) best = href;
  }
  return best;
}

const ROLE_LABEL: Record<Role, string> = {
  admin: 'role.admin',
  teacher: 'role.teacher',
  student: 'role.student',
  employee: 'role.employee',
};

function isVisible(item: NavItem, user: User | null | undefined, teaches: boolean): boolean {
  if (user?.isPosOperator) return posOperatorMayVisit(user, item.href);
  if (item.adminOnly && user?.role !== 'admin') return false;
  if (item.teachingOnly && !teaches && user?.role !== 'teacher') return false;
  if (item.hiddenWhenModule && canAccess(user, item.hiddenWhenModule)) return false;
  if (item.teachingStaffOnly) return !!user && (['admin', 'teacher'] as Role[]).includes(user.role);
  if (item.staffOnly) return !!user && (['admin', 'teacher', 'employee'] as Role[]).includes(user.role);
  if (item.anyModule) {
    return (item.orAdmin && user?.role === 'admin') || item.anyModule.some(module => canAccess(user, module));
  }
  return canAccess(user, item.module);
}

function NavLink({
  item,
  isActive,
  badge = 0,
  onNavigate,
}: {
  item: NavItem;
  isActive: boolean;
  badge?: number;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className={cn(
        'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      <item.icon className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate">{t(item.name)}</span>
      <AttentionBadge count={badge} tone={isActive ? 'onFilled' : 'default'} />
    </Link>
  );
}

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { logout, user } = useAuth();
  const version = cleanVersionTag(APP_VERSION);
  // The auth session carries no photo, so the avatar comes from the profile query —
  // the same cached entry the profile page fills, so a new photo shows up here as
  // soon as it is saved.
  const { data: profile } = useMyProfileQuery();
  const { byHref: badges } = useNavBadges();
  const location = useLocation();
  const activeHref = activeNavHref(location.pathname);
  const { t } = useTranslation();
  const { collapsed, toggle, expand } = useCollapsedSections();

  // Navigating into a collapsed section opens it, so the sidebar never looks like the page is
  // somewhere else — but only on the way in. Collapsing it while you sit there is respected.
  useEffect(() => {
    const holder = nav.find(entry => isSection(entry) && entry.items.some(i => i.href === activeHref));
    if (holder && isSection(holder)) expand(holder.section);
  }, [activeHref, expand]);
  // Asked of the timetable rather than the role, so an admin who teaches gets their classes.
  const { data: teaching } = useTeachesAnyQuery(canAccess(user));
  const teaches = teaching?.teaches ?? false;

  return (
    <div className="flex h-full flex-col">
      <Link
        to="/"
        onClick={onNavigate}
        title={t('nav.dashboard')}
        className="mx-3 my-2 flex min-h-14 flex-col justify-center rounded-lg px-2 py-2 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="text-lg font-bold tracking-tight text-primary">RISP</span>
        <span className="text-xs text-muted-foreground">{t('shell.staff_portal')}</span>
      </Link>

      <Separator />

      <nav className="flex-1 px-3 py-3 space-y-4">
        {nav.map(entry => {
          if (isSection(entry)) {
            const visible = entry.items.filter(i => isVisible(i, user, teaches));
            if (visible.length === 0) return null;
            // Open state is the stored choice, full stop. Deriving it from "holds the current
            // page" made the toggle look broken exactly where it is most wanted: on the section
            // you are already in, the click was recorded and then overridden on the next render.
            // Navigating into a collapsed section still opens it — see the effect above, which
            // runs on route changes rather than on every render.
            const isOpen = !collapsed.includes(entry.section);
            const pending = visible.reduce((sum, i) => sum + (badges[i.href] ?? 0), 0);
            return (
              <div key={entry.section}>
                <button
                  type="button"
                  onClick={() => toggle(entry.section)}
                  aria-expanded={isOpen}
                  // A 22px strip of 10px capitals was a target you had to aim at. Same
                  // typography, but a row the height of a nav item, with a hover surface so it
                  // reads as something you can press.
                  className="mb-1 flex min-h-9 w-full items-center gap-1.5 rounded-md px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', !isOpen && '-rotate-90')} />
                  <span className="truncate">{t(entry.section)}</span>
                  {/* Anything waiting stays visible while the section is shut, so collapsing
                      cannot hide work from someone. */}
                  {!isOpen && pending > 0 && (
                    <span className="ml-auto rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold text-primary">
                      {pending}
                    </span>
                  )}
                </button>
                {isOpen && (
                  <div className="space-y-0.5">
                    {visible.map(item => (
                      <NavLink
                        key={item.href}
                        item={item}
                        isActive={item.href === activeHref}
                        badge={badges[item.href]}
                        onNavigate={onNavigate}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          }
          if (!isVisible(entry, user, teaches)) return null;
          return (
            <div key={entry.href} className="space-y-0.5">
              <NavLink
                item={entry}
                isActive={entry.href === activeHref}
                badge={badges[entry.href]}
                onNavigate={onNavigate}
              />
            </div>
          );
        })}
      </nav>

      <Separator />

      <div className="p-3">
        <Link
          to="/my-profile"
          onClick={onNavigate}
          title={t('nav.my_profile')}
          className="group mb-2 flex items-center gap-2.5 rounded-lg border border-transparent bg-muted px-3 py-2.5 transition-colors hover:border-border hover:bg-muted/70"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/15 text-sm font-semibold uppercase text-primary">
            {profile?.photoUrl ? (
              <AdminImage src={profile.photoUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              ((user?.fullName?.trim() || user?.email)?.[0] ?? '?')
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground" title={user?.email}>
              {user?.fullName?.trim() || user?.email}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              <span className="group-hover:hidden">{user ? t(ROLE_LABEL[user.role]) : ''}</span>
              <span className="hidden group-hover:inline">{t('nav.my_profile')}</span>
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
        </Link>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
          onClick={logout}
        >
          <LogOut className="h-4 w-4" />
          {t('shell.sign_out')}
        </Button>
        {/* Which build is deployed. Absent on a dev build, where there is no version to report. */}
        {version && (
          <p className="pt-2 text-center text-[10px] text-muted-foreground/60" title={APP_VERSION}>
            v{version}
          </p>
        )}
      </div>
    </div>
  );
}

export function Sidebar() {
  return (
    <aside className="hidden lg:flex h-screen w-64 shrink-0 sticky top-0 flex-col border-r bg-background overflow-y-auto">
      <SidebarNav />
    </aside>
  );
}
